export interface BaseToastDefinition {
  id: string;
}

export type ToastStore<TToastDefinition extends BaseToastDefinition> = ReturnType<
  typeof createToastStore<TToastDefinition>
>;

type EventType = 'add' | 'remove';

interface AddEventPayload<TToastDefinition extends BaseToastDefinition> {
  toastDefinition: TToastDefinition;
}

interface RemoveEventPayload<TToastDefinition extends BaseToastDefinition> {
  toastDefinition: TToastDefinition;
}

export type ToastStoreChangeEvent<
  TEventType extends EventType,
  TToastDefinition extends BaseToastDefinition,
> = TEventType extends 'add'
  ? { type: TEventType } & AddEventPayload<TToastDefinition>
  : TEventType extends 'remove'
    ? { type: TEventType } & RemoveEventPayload<TToastDefinition>
    : never;

export type ToastStoreSubscriber<
  TEventType extends EventType,
  TToastDefinition extends BaseToastDefinition,
> = (event: ToastStoreChangeEvent<TEventType, TToastDefinition>) => void;

export function createToastStore<TToastDefinition extends BaseToastDefinition>() {
  const subscribers = new Map<
    EventType,
    Array<ToastStoreSubscriber<EventType, TToastDefinition>>
  >();

  const toastDefinitions: Array<TToastDefinition> = [];

  let snapshot: ReadonlyArray<TToastDefinition> = [];

  // For the `addToast` method, we don't require the `id` property to be provided
  // in the definition object. If no `id` is provided, we generate a random id.
  // So we need to adjust the argument type to reflect this behavior.
  const addToast = (
    definition: Omit<TToastDefinition, 'id'> & {
      /**
       * If no `id` is provided, a random id will be generated
       * when the toast is added to the store.
       */
      id?: string;
    }
  ) => {
    const definitionId = definition.id ?? `toast-${Date.now()}`;

    // We have to use a type assertion here because TS can't infer that
    // the `id` property is now defined
    const resolvedDefinition = {
      ...definition,
      id: definitionId,
    } as TToastDefinition;

    toastDefinitions.push(resolvedDefinition);

    snapshot = Array.from(toastDefinitions);

    subscribers
      .get('add')
      ?.forEach((subscriber) => subscriber({ type: 'add', toastDefinition: resolvedDefinition }));

    return definitionId;
  };

  const removeToast = (toastDefinitionId: string) => {
    const index = toastDefinitions.findIndex((definition) => definition.id === toastDefinitionId);
    if (index === -1) {
      return;
    }

    const removedDefinition = toastDefinitions.splice(index, 1)[0];

    snapshot = Array.from(toastDefinitions.values());

    subscribers
      .get('remove')
      ?.forEach((subscriber) => subscriber({ type: 'remove', toastDefinition: removedDefinition }));
  };

  // We return a referentially stable snapshot of the toasts so that
  // subscribers can compare the previous snapshot to the current snapshot
  // to determine if the values have changed.
  // note: this is primarily intended for React `useSyncExternalStore`,
  // but doesn't hurt to have it available for other use cases.
  const getToasts = () => {
    return snapshot;
  };

  const addSubscriber = <TEventType extends EventType>(
    eventType: TEventType,
    subscriber: ToastStoreSubscriber<TEventType, TToastDefinition>
  ) => {
    const existingSubscribers = subscribers.get(eventType) ?? [];
    // We cast as `any` here because TS complains that there is no
    // guarantee that `subscribers.get(eventType)` matches the type of
    // `subscriber`. However, we know it is accurate because we are
    // using `eventType` as the map key. This is a limitation
    // of Map types in TS.
    subscribers.set(eventType, [...existingSubscribers, subscriber as any]);
  };

  const removeSubscriber = <TEventType extends EventType>(
    eventType: TEventType,
    subscriber: ToastStoreSubscriber<TEventType, TToastDefinition>
  ) => {
    const existingSubscribers = subscribers.get(eventType);
    if (existingSubscribers) {
      // We cast as `any` here because TS complains that there is no
      // guarantee that `subscribers.get(eventType)` matches the type of
      // `subscriber`. However, we know it is accurate because we are
      // using `eventType` as the map key. This is a limitation
      // of Map types in TS.
      existingSubscribers.splice(existingSubscribers.indexOf(subscriber as any), 1);
    }
  };

  const onChange = <TEventType extends EventType>(
    eventType: TEventType,
    subscriber: ToastStoreSubscriber<TEventType, TToastDefinition>
  ) => {
    addSubscriber(eventType, subscriber);

    return () => {
      removeSubscriber(eventType, subscriber);
    };
  };

  return {
    addToast,
    removeToast,
    getToasts,
    onChange,
  };
}
